import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, Cpu, MemoryStick, Clock } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const StatCard = ({ icon: Icon, label, value, color, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${color.bg} flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${color.text}`} />
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className={`text-lg font-bold ${color.value || color.text}`}>{value}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default function OverviewStats({ servers }) {
  const { t } = useLanguage();

  const safeServers = Array.isArray(servers) ? servers : [];
  const effectiveStatus = (server) => (!server?.isDataReal ? "disabled" : server?.status || "offline");
  const activeServers = safeServers.filter((s) => s?.isDataReal);

  const healthyCount = safeServers.filter((s) => effectiveStatus(s) === "healthy").length;
  const warningCount = safeServers.filter((s) => effectiveStatus(s) === "warning").length;
  const criticalCount = safeServers.filter((s) => effectiveStatus(s) === "critical").length;
  const activeServersCount = activeServers.length;

  const avgCpu = activeServersCount
    ? Math.round(activeServers.reduce((sum, s) => sum + (Number(s.cpu) || 0), 0) / activeServersCount)
    : 0;
  const avgMemory = activeServersCount
    ? Math.round(activeServers.reduce((sum, s) => sum + (Number(s.memory) || 0), 0) / activeServersCount)
    : 0;
  const avgLatency = activeServersCount
    ? Math.round(activeServers.reduce((sum, s) => sum + (Number(s.latency) || 0), 0) / activeServersCount)
    : 0;

  const stats = [
    {
      icon: CheckCircle2,
      label: t("healthy"),
      value: `${healthyCount}/${activeServersCount}`,
      color: { bg: "bg-emerald-100", text: "text-emerald-600", value: "text-emerald-600" },
      delay: 0,
    },
    {
      icon: AlertTriangle,
      label: t("warnings"),
      value: warningCount,
      color: { bg: "bg-amber-100", text: "text-amber-600", value: "text-amber-600" },
      delay: 0.05,
    },
    {
      icon: AlertTriangle,
      label: t("critical"),
      value: criticalCount,
      color: { bg: "bg-red-100", text: "text-red-600", value: "text-red-600" },
      delay: 0.1,
    },
    {
      icon: Cpu,
      label: t("avgCpu"),
      value: `${avgCpu}%`,
      color: { bg: "bg-blue-100", text: "text-blue-600" },
      delay: 0.15,
    },
    {
      icon: MemoryStick,
      label: t("avgMemory"),
      value: `${avgMemory}%`,
      color: { bg: "bg-purple-100", text: "text-purple-600" },
      delay: 0.2,
    },
    {
      icon: Clock,
      label: t("avgLatency"),
      value: `${avgLatency}ms`,
      color: { bg: "bg-pink-100", text: "text-pink-600" },
      delay: 0.25,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
      {stats.map((stat, index) => (
        <StatCard
          key={index}
          icon={stat.icon}
          label={stat.label}
          value={stat.value}
          color={stat.color}
          delay={stat.delay}
        />
      ))}
    </div>
  );
}
